import React, { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import {
  getManualEvents,
  createManualEvent,
  updateManualEvent,
  deleteManualEvent,
  type ManualEventRow,
  type ManualEventInput,
} from '../../services/manualEventService';
import { getSessionPin, setSessionPin, verifyAdminPIN } from '../../services/pinAuthService';
import styles from './ManageEvents.module.css';

const EMPTY_FORM: ManualEventInput = {
  name: '',
  description: '',
  start_time: '',
  end_time: '',
  venue_name: '',
  venue_address: '',
  image_url: '',
  link_url: '',
  button_label: 'TICKETS',
  is_sold_out: false,
};

// datetime-local inputs need "YYYY-MM-DDTHH:mm" with no timezone suffix
function toLocalInputValue(isoString: string): string {
  if (!isoString) return '';
  const date = new Date(isoString);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

const PinPrompt: React.FC<{
  onCancel: () => void;
  onVerified: (pin: string) => void;
}> = ({ onCancel, onVerified }) => {
  const [pin, setPin] = useState('');
  const [checking, setChecking] = useState(false);
  const [pinError, setPinError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setChecking(true);
    setPinError(null);

    const ok = await verifyAdminPIN(pin);

    if (!ok) {
      setChecking(false);
      setPinError('PIN not recognized, or that user is not an admin');
      return;
    }

    setSessionPin(pin);
    onVerified(pin);
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h2>Confirm PIN</h2>
          <button onClick={onCancel} className={styles.closeButton}>×</button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.field}>
            <label htmlFor="confirm_pin">Re-enter your admin PIN to save changes</label>
            <input
              id="confirm_pin"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              autoFocus
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              disabled={checking}
              required
            />
          </div>

          {pinError && <div className={styles.error}>{pinError}</div>}

          <div className={styles.actions}>
            <button type="button" onClick={onCancel} disabled={checking} className={styles.cancelButton}>
              Cancel
            </button>
            <button type="submit" disabled={checking || !pin} className={styles.submitButton}>
              {checking ? 'Checking...' : 'Confirm'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const ManageEvents: React.FC = () => {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ManualEventInput>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  // Set when a write is attempted without a PIN in memory (i.e. after a page
  // refresh); holds the action to resume once the PIN has been re-entered.
  const [pendingWrite, setPendingWrite] = useState<((pin: string) => void) | null>(null);

  const { data: eventsResult, isLoading } = useQuery({
    queryKey: ['manual-events'],
    queryFn: getManualEvents,
  });

  const events = eventsResult?.data || [];

  const refetch = () => queryClient.invalidateQueries({ queryKey: ['manual-events'] });

  // Every write needs the admin PIN the RPCs verify. It lives in memory only,
  // so if a refresh cleared it, park the action and prompt for the PIN first.
  const withPin = (action: (pin: string) => void) => {
    const pin = getSessionPin();
    if (pin) {
      action(pin);
      return;
    }
    setPendingWrite(() => action);
  };

  const createMutation = useMutation({
    mutationFn: ({ input, pin }: { input: ManualEventInput; pin: string }) =>
      createManualEvent(input, pin),
    onSuccess: ({ error: createError }) => {
      if (createError) {
        setError(createError.message || 'Failed to create event');
        return;
      }
      refetch();
      handleCloseForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input, pin }: { id: string; input: ManualEventInput; pin: string }) =>
      updateManualEvent(id, input, pin),
    onSuccess: ({ error: updateError }) => {
      if (updateError) {
        setError(updateError.message || 'Failed to update event');
        return;
      }
      refetch();
      handleCloseForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: ({ id, pin }: { id: string; pin: string }) => deleteManualEvent(id, pin),
    onSuccess: ({ error: deleteError }) => {
      if (deleteError) {
        setError(deleteError.message || 'Failed to delete event');
        return;
      }
      refetch();
    },
  });

  const handleOpenCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setError(null);
    setShowForm(true);
  };

  const handleOpenEdit = (row: ManualEventRow) => {
    setEditingId(row.id);
    setForm({
      name: row.name,
      description: row.description || '',
      start_time: toLocalInputValue(row.start_time),
      end_time: toLocalInputValue(row.end_time),
      venue_name: row.venue_name || '',
      venue_address: row.venue_address || '',
      image_url: row.image_url || '',
      link_url: row.link_url,
      button_label: row.button_label,
      is_sold_out: row.is_sold_out,
    });
    setError(null);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setError(null);
  };

  const handleDelete = (id: string) => {
    if (confirm('Delete this event? This cannot be undone.')) {
      setError(null);
      withPin((pin) => deleteMutation.mutate({ id, pin }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!form.name || !form.start_time || !form.end_time || !form.link_url) {
      setError('Name, start time, end time, and link URL are required');
      return;
    }

    const input: ManualEventInput = {
      ...form,
      start_time: new Date(form.start_time).toISOString(),
      end_time: new Date(form.end_time).toISOString(),
      button_label: form.button_label || 'TICKETS',
    };

    if (editingId) {
      withPin((pin) => updateMutation.mutate({ id: editingId, input, pin }));
    } else {
      withPin((pin) => createMutation.mutate({ input, pin }));
    }
  };

  const saving = createMutation.isPending || updateMutation.isPending;

  const formatDateTime = (iso: string) =>
    new Date(iso).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1>Manage Events</h1>
        <button onClick={handleOpenCreate} className={styles.addButton}>
          + ADD EVENT
        </button>
      </div>

      <p className={styles.hint}>
        Manual events appear on the public event list alongside Eventbrite events. They're not
        touched by the Eventbrite sync, so they'll stay until you edit or delete them here.
      </p>

      {/* Form errors render inside the modal; this catches delete failures */}
      {error && !showForm && <div className={styles.error}>{error}</div>}

      {isLoading ? (
        <div className={styles.loading}>Loading...</div>
      ) : events.length === 0 ? (
        <div className={styles.emptyState}>
          <p>No manual events yet</p>
          <button onClick={handleOpenCreate} className={styles.addButton}>
            ADD YOUR FIRST EVENT
          </button>
        </div>
      ) : (
        <div className={styles.eventList}>
          {events.map((event) => (
            <div key={event.id} className={styles.eventItem}>
              <div className={styles.eventInfo}>
                <span className={styles.eventName}>
                  {event.name}
                  {event.is_sold_out && <span className={styles.soldOutBadge}>SOLD OUT</span>}
                </span>
                <span className={styles.eventMeta}>{formatDateTime(event.start_time)}</span>
                <span className={styles.eventMeta}>{event.venue_name || 'No venue set'}</span>
                <span className={styles.eventLink} title={event.link_url}>
                  {event.button_label} → {event.link_url}
                </span>
              </div>
              <div className={styles.eventActions}>
                <button onClick={() => handleOpenEdit(event)} className={styles.actionButton}>
                  EDIT
                </button>
                <button
                  onClick={() => handleDelete(event.id)}
                  className={styles.deleteButton}
                  disabled={deleteMutation.isPending}
                >
                  DELETE
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        // Deliberately no click-outside-to-close: on mobile the keyboard shifts
        // the layout mid-tap, so outside clicks land here by accident and would
        // silently discard the form. Close via the X or Cancel buttons only.
        <div className={styles.overlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h2>{editingId ? 'Edit Event' : 'Add Event'}</h2>
              <button onClick={handleCloseForm} className={styles.closeButton}>×</button>
            </div>

            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.field}>
                <label htmlFor="name">Event Name *</label>
                <input
                  id="name"
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Presale: Summer Kickoff"
                  disabled={saving}
                  required
                />
              </div>

              <div className={styles.fieldGroup}>
                <div className={styles.field}>
                  <label htmlFor="start_time">Start *</label>
                  <input
                    id="start_time"
                    type="datetime-local"
                    value={form.start_time}
                    onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                    disabled={saving}
                    required
                  />
                </div>
                <div className={styles.field}>
                  <label htmlFor="end_time">End *</label>
                  <input
                    id="end_time"
                    type="datetime-local"
                    value={form.end_time}
                    onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                    disabled={saving}
                    required
                  />
                </div>
              </div>

              <div className={styles.field}>
                <label htmlFor="venue_name">Venue Name</label>
                <input
                  id="venue_name"
                  type="text"
                  value={form.venue_name}
                  onChange={(e) => setForm({ ...form, venue_name: e.target.value })}
                  placeholder="REFUGE"
                  disabled={saving}
                />
              </div>

              <div className={styles.field}>
                <label htmlFor="venue_address">Venue Address</label>
                <input
                  id="venue_address"
                  type="text"
                  value={form.venue_address}
                  onChange={(e) => setForm({ ...form, venue_address: e.target.value })}
                  placeholder="123 Main St, Brooklyn, NY"
                  disabled={saving}
                />
              </div>

              <div className={styles.field}>
                <label htmlFor="image_url">Flyer/Image URL</label>
                <input
                  id="image_url"
                  type="url"
                  value={form.image_url}
                  onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                  placeholder="https://..."
                  disabled={saving}
                />
              </div>

              <div className={styles.sectionDivider}>
                <span>Button</span>
              </div>

              <div className={styles.fieldGroup}>
                <div className={styles.field}>
                  <label htmlFor="link_url">External Link *</label>
                  <input
                    id="link_url"
                    type="url"
                    value={form.link_url}
                    onChange={(e) => setForm({ ...form, link_url: e.target.value })}
                    placeholder="https://..."
                    disabled={saving}
                    required
                  />
                </div>
                <div className={styles.field}>
                  <label htmlFor="button_label">Button Text</label>
                  <input
                    id="button_label"
                    type="text"
                    value={form.button_label}
                    onChange={(e) => setForm({ ...form, button_label: e.target.value })}
                    placeholder="TICKETS"
                    disabled={saving}
                  />
                </div>
              </div>

              <div className={styles.field}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={form.is_sold_out}
                    onChange={(e) => setForm({ ...form, is_sold_out: e.target.checked })}
                    disabled={saving}
                  />
                  Mark as sold out
                </label>
              </div>

              {error && <div className={styles.error}>{error}</div>}

              <div className={styles.actions}>
                <button type="button" onClick={handleCloseForm} disabled={saving} className={styles.cancelButton}>
                  Cancel
                </button>
                <button type="submit" disabled={saving} className={styles.submitButton}>
                  {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Create Event'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Rendered last so it stacks above the event form when both are open */}
      {pendingWrite && (
        <PinPrompt
          onCancel={() => setPendingWrite(null)}
          onVerified={(pin) => {
            const resume = pendingWrite;
            setPendingWrite(null);
            resume(pin);
          }}
        />
      )}
    </div>
  );
};
